import {
  Component,
  ComponentRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseButtonComponent } from '@d-components/button/button.component';
import {
  combineLatest,
  debounce,
  distinctUntilChanged,
  Observable,
  Subject,
  takeUntil,
  timer,
} from 'rxjs';
import {
  BaseButtonFormConfig,
  BASE_FORM_ITEMS_EVENT,
  BASE_UI_TOKEN,
  CONTROL_TYPE,
  FormConfig,
  FORM_ARRAY_TOKEN,
  isBaseButton,
} from './form.interface';

@Component({
  selector: 'app-form-item',
  template: '<ng-container #formItemContainer></ng-container>',
  standalone: true,
})
export class FormItemComponent implements OnInit, OnDestroy {
  @ViewChild('formItemContainer', { read: ViewContainerRef, static: true })
  formItemContainer!: ViewContainerRef;
  private componentRef!: ComponentRef<any>;
  destroy$ = new Subject();

  @Input() fieldConfig!: FormConfig;
  @Input() parentFieldConfig!: FormConfig[];

  constructor(
    @Inject(BASE_UI_TOKEN) private allBaseUIComp: any,
    @Inject(FORM_ARRAY_TOKEN) private formArray: any,
  ) {}

  ngOnInit(): void {
    this.formItemContainer.clear();
    this.renderItem();
  }

  private renderItem() {
    this.componentRef = this.formItemContainer.createComponent<any>(
      this.allBaseUIComp[this.fieldConfig.type] ||
        this.formArray[this.fieldConfig.type],
    );

    Object.keys(this.fieldConfig).forEach((i: any) => {
      this.componentRef.instance[i] = (this.fieldConfig as any)[i];
    });
    this.componentRef.instance.parentFieldConfig = this.parentFieldConfig;
    this.componentRef.instance.disabled =
      this.fieldConfig.fieldControl?.disabled || this.fieldConfig.disabled;

    if (!this.fieldConfig.fieldControl) {
      this.componentRef.instance.fieldControl = new FormControl('');
    }

    if (this.fieldConfig.dependOnFields?.length) {
      this.invokeDependent();
    }

    if (isBaseButton(this.fieldConfig)) {
      (this.componentRef.instance as BaseButtonComponent).onClick
        .pipe(takeUntil(this.destroy$))
        .subscribe((val) => {
          (this.fieldConfig as BaseButtonFormConfig).onClickFunc(val);
        });
    }

    return this.componentRef.instance;
  }

  private invokeDependent() {
    this.fieldConfig.dependOnFields?.forEach((deps) => {
      if (
        (this.fieldConfig.type === CONTROL_TYPE.Dropdown ||
          this.fieldConfig.type === CONTROL_TYPE.Multiselect) &&
        (deps as any).updateOptions
      ) {
        return;
      }
      const trackingField = deps.fields.map((field) => {
        if (Object.values(BASE_FORM_ITEMS_EVENT).includes(field as string)) {
          switch (field) {
            case BASE_FORM_ITEMS_EVENT.OPTIONS_CHANGE:
              return this.componentRef.instance['optionUpdated'];
          }
        }
        if (typeof field === 'string') {
          return this.parentFieldConfig.find((i) => i.id === field)
            ?.fieldControl?.valueChanges;
        }
        return field;
      });

      combineLatest(trackingField as Observable<any>[])
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(),
          debounce((_) => timer(deps.debounce || 0)),
        )
        .subscribe((_) => {
          deps.action(this.fieldConfig as any, this.componentRef.instance);

          Object.keys(this.fieldConfig).forEach((i: any) => {
            this.componentRef.instance[i] = (this.fieldConfig as any)[i];
          });
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
